import { Music, Smartphone, Speaker, Video, Sliders, Sprout, TrendingUp, Award, Radio, Crown } from "lucide-react";
import { cn } from "@/lib/utils";

interface Show {
  id: number;
  date: string;
  venue: {
    id: number;
    name: string;
    city?: string | null;
    state?: string | null;
  };
  artist?: {
    id: number;
    name: string;
    imageUrl?: string | null;
    genres?: string[] | null;
    careerStage?: string | null;
  } | null;
}

interface ShowPackage {
  showId: number;
  setCore: boolean;
  setAudio: boolean;
  setVideo: boolean;
}

interface PlanTimelineItemProps {
  show: Show;
  showPackages: Record<number, ShowPackage>;
  showCount?: number; // For multi-show events on same date
}

export function PlanTimelineItem({ show, showPackages, showCount }: PlanTimelineItemProps) {
  // Get package tier for this show
  const getPackageTier = (): 'set-core' | 'set-audio' | 'set-video' | 'custom' | null => {
    const pkg = showPackages[show.id];
    if (!pkg) return null;

    const activePackages = [pkg.setCore, pkg.setAudio, pkg.setVideo].filter(Boolean).length;

    // If no standard packages are active, it's a custom package
    if (activePackages === 0) return 'custom';

    if (activePackages > 1) return 'set-video'; // Multiple packages = treat as set-video for color
    if (pkg.setVideo) return 'set-video';
    if (pkg.setAudio) return 'set-audio';
    if (pkg.setCore) return 'set-core';

    return null;
  };

  const packageTier = getPackageTier();

  // Get package styling based on tier
  const getPackageStyles = () => {
    switch (packageTier) {
      case 'set-core':
        return {
          borderColor: 'border-l-gray-600',
          badgeColor: 'bg-gray-600',
          dateBoxBg: 'bg-gray-600',
          icon: Smartphone,
          label: 'SET Core'
        };
      case 'set-audio':
        return {
          borderColor: 'border-l-blue-600',
          badgeColor: 'bg-blue-600',
          dateBoxBg: 'bg-blue-600',
          icon: Speaker,
          label: 'SET Audio'
        };
      case 'set-video':
        return {
          borderColor: 'border-l-purple-600',
          badgeColor: 'bg-gradient-to-r from-purple-600 to-blue-600',
          dateBoxBg: 'bg-purple-600',
          icon: Video,
          label: 'SET Video'
        };
      case 'custom':
        return {
          borderColor: 'border-l-green-600',
          badgeColor: 'bg-green-600',
          dateBoxBg: 'bg-green-600',
          icon: Sliders,
          label: 'SET Custom'
        };
      default:
        return {
          borderColor: 'border-l-pink-500/40',
          badgeColor: 'bg-gray-500',
          dateBoxBg: 'bg-gray-500',
          icon: Music,
          label: 'Package'
        };
    }
  };

  const packageStyles = getPackageStyles();

  // Format date for calendar box (using UTC to avoid timezone issues)
  const date = new Date(show.date);
  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }).toUpperCase();
  const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase();
  const day = date.getUTCDate();

  // Get first genre from array
  const genre = show.artist?.genres && show.artist.genres.length > 0
    ? show.artist.genres[0]
    : null;

  // Format career stage for display (remove number suffix)
  const formatCareerStage = (stage: string | null | undefined) => {
    if (!stage) return null;
    // "Emerging" -> "Emerging", "Established 1" -> "Established", etc.
    return stage.split(' ')[0];
  };

  // Get career stage icon
  const getCareerStageIcon = (stage: string | null) => {
    const lowerStage = stage?.toLowerCase();
    if (lowerStage === 'emerging') return Sprout;
    if (lowerStage === 'breakthrough') return TrendingUp;
    if (lowerStage === 'established') return Award;
    if (lowerStage === 'mainstream') return Crown;
    return Radio;
  };

  const careerStage = formatCareerStage(show.artist?.careerStage);
  const CareerStageIcon = careerStage ? getCareerStageIcon(careerStage) : null;

  return (
    <div className={cn(
      "relative border border-pink-200/30 rounded-lg p-3",
      "hover:bg-gradient-to-r hover:from-pink-50/50 hover:to-blue-50/50",
      "hover:shadow-md hover:scale-[1.01] transition-all duration-200",
      "border-l-4",
      packageStyles.borderColor
    )}>
      <div className="flex items-center gap-3">
        {/* Calendar Date Box */}
        <div className={cn(
          "flex-shrink-0 w-16 h-16 rounded-lg flex flex-col items-center justify-center text-white shadow-md",
          packageStyles.dateBoxBg
        )}>
          <div className="text-[11px] font-semibold leading-none">{month}</div>
          <div className="text-2xl font-bold leading-none mt-1">{day}</div>
          <div className="text-[8px] font-light leading-none mt-0.5 opacity-80">{dayOfWeek}</div>
        </div>

        {/* Artist Thumbnail */}
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-purple-100 to-pink-200">
            {show.artist?.imageUrl ? (
              <img
                src={show.artist.imageUrl}
                alt={show.artist.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xl">
                🎤
              </div>
            )}
          </div>
        </div>

        {/* Artist Info (stacked) */}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-gray-900 truncate">
            {show.artist?.name || "Unknown Artist"}
          </div>
          {genre && (
            <div className="flex items-center gap-1.5 text-[10px] text-gray-600">
              <Music className="w-3 h-3 text-gray-500 flex-shrink-0" />
              <span className="truncate">{genre}</span>
            </div>
          )}
          {careerStage && (
            <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
              {CareerStageIcon && <CareerStageIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />}
              <span className="font-medium">{careerStage}</span>
            </div>
          )}
        </div>

        {/* Venue Info (stacked) */}
        <div className="flex-shrink-0 text-right min-w-0 max-w-[200px]">
          <div className="text-sm font-medium text-gray-900 truncate">
            {show.venue.name}
          </div>
          {show.venue.city && show.venue.state && (
            <div className="text-xs text-gray-600 truncate">
              {show.venue.city}, {show.venue.state}
            </div>
          )}
        </div>

        {/* Package Badge (right-aligned) */}
        <div className="flex-shrink-0">
          <div className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold shadow-sm",
            packageStyles.badgeColor
          )}>
            <packageStyles.icon className="w-3 h-3" />
            {packageStyles.label}
          </div>
        </div>

        {/* Show count badge for multi-show dates */}
        {showCount && showCount > 1 && (
          <div className="absolute -top-2 -right-2 flex-shrink-0">
            <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full border-2 border-white shadow-sm">
              {showCount}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
